import { ILogEntry } from "motw_core/Entity/ILogEntry";
import { HandleError } from "source/app/presentation/Shared/HandleError";
import { IDataCall } from "../infrastructure/Data/IDataCall";
import { WithAnimatedProgressBarAsync } from "../presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { LoadEmotionCharacterKVAsync } from "../presentation/MainPanel/KeyValueLogs/KeyValueLogs";
import { AddStatusMessage } from "../presentation/MainPanel/StatusBar/StatusBar";
import { EStatusMessageType } from "../presentation/MainPanel/StatusBar/StatusMessageType/StatusMessageType";
import MotwLogicServer from "./External/LogicServer/MotwLogicServer";
import MediaServer from "./External/MediaServer";
import { GetRecentLogEntriesAsync } from "motw_core/Repositories/LogRepository";
import { ESharedComponent } from "./UI/State/ESharedComponent";
import UI from "./UI/UI";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { SetState } from "source/app/services/UI/State/SetState";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import * as moment from "moment";
import uniq from "lodash-es/uniq";
import { ISyncNotificationState } from "../presentation/Header/Center/Sync/ISyncNotificationState";

let calls: IDataCall[] = [];
const unmountedComponents = new WeakSet<React.Component>();

const Data = {
    RecentLogEntries: 12,

    Load: (function () {
        const Load = {
            Component: async (call: IDataCall) => {
                if (!call.inLoadAll) {
                    call.inLoadAll = false;
                }

                unmountedComponents.delete(call.component);

                const name = call.component.constructor.name;
                if (!call.load) call.load = name;

                let data: any[] = [];

                // Note: Extracting this method causes
                // duplicate network requests to occur
                // TOOD: figure out why this happens
                const GetCallData = async () => {
                    // resource is a function
                    const resourceAction: Function = <Function>call.load;
                    data = await resourceAction();

                    if (data === undefined) {
                        return;
                    }

                    StoreDataInState(call, data);
                };

                await WithAnimatedProgressBarAsync(GetCallData);

                if (unmountedComponents.has(call.component)) {
                    return data;
                }

                const hasComponentBeenCalledBefore =
                    calls.filter(
                        (component) => component.constructor.name === name
                    ).length > 0;

                if (
                    !hasComponentBeenCalledBefore &&
                    call.inLoadAll &&
                    !unmountedComponents.has(call.component)
                ) {
                    calls.push(call);
                }

                return data;
            },

            Calls: {
                Distinct: () => {
                    if (!calls.length) {
                        throw new Error(
                            "No calls found when trying to load all Data"
                        );
                    }

                    return uniq(calls);
                },

                ExecuteAsync: (calls: IDataCall[]) => {
                    const callActions = calls.map((call) =>
                        Data.Load.Component(call)
                    );

                    return WithAnimatedProgressBarAsync(() =>
                        Promise.all(callActions)
                    );
                },
            },

            AllAsync: async () => {
                try {
                    console.log(`[Data] Started loading all data...`);

                    SetState<ISyncNotificationState>(
                        ESharedFunction.SyncNotification_state,
                        { isOutOfSync: true }
                    );

                    const filteredCalls = Data.Load.Calls.Distinct();
                    await Data.Load.Calls.ExecuteAsync(filteredCalls);

                    // Load functional component data
                    await LoadEmotionCharacterKVAsync();

                    SetState<ISyncNotificationState>(
                        ESharedFunction.SyncNotification_state,
                        { lastLoadAllTime: moment().format() }
                    );
                } finally {
                    SetState<ISyncNotificationState>(
                        ESharedFunction.SyncNotification_state,
                        { isOutOfSync: false }
                    );

                    console.log(`[Data] Finished loading all data`);
                }
            },

            Single: {
                ByResource: (sharedComponent: ESharedComponent) => {
                    const componentName = ESharedComponent[sharedComponent];
                    const distinctCalls: IDataCall[] =
                        Data.Load.Calls.Distinct();

                    const relevantCalls = distinctCalls.filter(
                        (call: IDataCall) => call.load === componentName
                    );

                    const availableCallLoadNames = distinctCalls
                        .filter((call) => typeof call.load === "string")
                        .map((call) => call.load)
                        .join(", ");

                    if (!relevantCalls.length) {
                        throw new Error(
                            `Cannot find a data call "load" property value that equals '${componentName}'. The available values are: '${availableCallLoadNames}'. Alternatively, try using 'ByStateTarget()' to identify the data call.`
                        );
                    }

                    Data.Load.Calls.ExecuteAsync(relevantCalls);
                },

                ByStateTarget: <TComponentState>(
                    stateTarget: keyof TComponentState
                ) => {
                    const filteredCalls: IDataCall[] =
                        Data.Load.Calls.Distinct();

                    const singleCall = filteredCalls.filter(
                        (call: IDataCall) => call.stateTarget === stateTarget
                    );

                    return Data.Load.Calls.ExecuteAsync(singleCall);
                },
            },
        };

        return Load;
    })(),

    Initialize: (function () {
        return {
            Log: [
                {
                    id: 0,
                    data: [
                        "+ loading...",
                        "0₭",
                        "0.0",
                        "2017-04-14 00:00",
                        "0",
                    ],
                },
            ],
            StartingDate: "2017-04-14T00:00:00.000Z",
            Destiny: [{ year: 0, text: "Loading...", rating: 0 }],
        };
    })(),

    GetValue: (function () {
        return {
            Balance: async () => {
                const log: ILogEntry[] = await GetRecentLogEntriesAsync(
                    Data.RecentLogEntries,
                    ClientDataProvider
                );

                const recentDrives: string[] = log.map((row) =>
                    UI.GetEventCategory(row.entry)
                );

                const drive1Count = recentDrives.filter(
                    (symbol) => symbol === "^"
                ).length;

                const drive2Count = recentDrives.filter(
                    (symbol) => symbol === "*"
                ).length;

                const drive3Count = recentDrives.filter(
                    (symbol) => symbol === "+"
                ).length;

                const drive4Count = recentDrives.filter(
                    (symbol) => symbol === "#"
                ).length;

                const balance =
                    Math.min(drive1Count, 5) +
                    Math.min(drive2Count, 5) +
                    Math.min(drive3Count, 5) +
                    Math.min(drive4Count, 5);
                const result = (balance * 5).toFixed(0);
                return result;
            },
        };
    })(),

    GetLevelFromKips: (kips: number) => Math.floor(0.1 * Math.sqrt(kips)),

    GetKipsToReachLevel: (level: number) => Math.pow(level / 0.1, 2),

    GetKipsToLevelUp: () =>
        Promise.all([
            MotwLogicServer.GetPlayerLevelCurrent(),
            MotwLogicServer.GetPlayerLifetimeKipsEarned(),
        ]).then(
            (values) => Data.GetKipsToReachLevel(values[0] + 1) - values[1]
        ),

    LoadFromServer: (route: string, callback?: Function) => {
        if (!route) throw new Error("missing input parameters!");
        if (!callback) callback = (data: any) => data; // return result if no callback provided

        return MediaServer.Get(route)
            .then((data) => (callback ? callback(data) : {}))
            .catch(HandleError);
    },

    Unload: (component: React.Component) => UnloadDataCall(component),
};

export default Data;

function UnloadDataCall(component: React.Component) {
    unmountedComponents.add(component);
    calls = calls.filter((call) => call.component !== component);
}

function StoreDataInState(call: IDataCall, data: any[]) {
    if (unmountedComponents.has(call.component)) {
        return;
    }

    const { stateTarget } = call;

    if (!stateTarget) {
        return;
    }

    const isNestedTarget = stateTarget.includes(`.`);

    if (isNestedTarget) {
        const parent = stateTarget.split(`.`)[0];
        const child = stateTarget.split(`.`)[1];

        call.component.setState({
            [parent]: {
                [child || `data`]: data,
            },
        });
    } else {
        call.component.setState({
            [stateTarget || "data"]: data,
        });
    }
}
